import { DurationInputArg1, DurationInputArg2 } from 'moment';

import Action from '../action';
import { getAppConfig } from '../apiClient';
import { UaopFlags } from '../beneficiaries/fileUploadPages/DryRunDialog';
import { getAppConfig as getAppPartnerConfig } from '../partnerPanel/apiClient';
import { ProductTabs } from '../products/helpers';
import { UserStatuses } from '../utils/users';

export type CustomExampleFilePerFlag = Record<string, string>;

export interface SingleCategoryConfig {
    code: string;
    name: string;
    metaCategoryId: number;
    orderInMetaCategory?: number;

    allowImport: boolean;
    useEnterpriseConfirmationText: boolean;
    deduplicateByFamily: boolean;
    transactionReportWarning?: string;

    interCategoryDeduplication:
        | { enabled: false }
        | {
              enabled: true;
              recommendedCategoryCode?: string;
              optionalCategoryCodes: string[];
              mandatoryCategoryCodes: string[];
          };

    customDeduplicationRowReportLabel?: string;
    customDeduplicationStatsLabel?: string;

    amountValidation:
        | { enabled: false }
        | {
              enabled: true;
              minMax:
                  | { enabled: false }
                  | {
                        enabled: true;
                        minimum?: number;
                        maximum?: number;
                        validationLevel: string;
                        messageShowHousehold: boolean;
                        messageShowCategory: boolean;
                    };
              recommendedMinimum:
                  | { enabled: false }
                  | {
                        enabled: true;
                        value: number;
                        validationLevel: string;
                        customMessage?: string;
                    };
              divisibleBy:
                  | { enabled: false }
                  | {
                        enabled: true;
                        value: number;
                        validationLevel: string;
                    };
          };

    dateValidation:
        | { enabled: false }
        | {
              enabled: true;
              allowedEntitlementPeriodsInMonths?: number[];
              enforceFirstDayOfMonthStart: boolean;
              enforceLastDayOfMonthEnd: boolean;
              enforceFirstDayOfYearStart: boolean;
              enforceLastDayOfYearEnd: boolean;
              disallowPastStartDate: boolean;
              enforceCurrentOrNextYear: boolean;
              fixedValidityPeriods?: { startDate: string; endDate: string }[];
          };

    strategies: {
        flag: string;
        isRecommended: boolean;
        prededupHouseholdOverlap: boolean;
        exampleFileUrl: { enabled: false } | { enabled: true; url: string };
    }[];
}

export interface SingleMetaCategory {
    id: number;
    name: string;
    abbreviation: string;
    order: number;
}

export interface Permission {
    permissionName: string;
    metadata: {
        displayText: string;
        explanation: string;
        requiredPermissions: string[];
        section: string;
    };
}

export enum BeneficiaryPrimaryIdentifierType {
    TAX_ID = 'TAX_ID',
    COMMON_IDENTIFIER = 'COMMON_IDENTIFIER',
}

export const BeneficiaryPrimaryIdentifierToShow: Record<BeneficiaryPrimaryIdentifierType, string> = {
    COMMON_IDENTIFIER: 'Common Identifier',
    TAX_ID: 'Tax ID',
};

export const BeneficiaryPrimaryIdentifierAbbreviation: Record<BeneficiaryPrimaryIdentifierType, string> = {
    COMMON_IDENTIFIER: 'CID',
    TAX_ID: 'TID',
};

export class AppConfigState {
    entitlementsConfig: EntitlementCategoriesConfig;
    sapConfig: SapConfig;
    entitlementCurrencyConfig: EntitlementCurrencyConfig;
    paymentConfig: PaymentConfig;
    country: string;
    timeZone: string;
    permissions: {
        [key: string]: Permission[];
    };
    initialTransactionDate: string;
    productManagementConfig: { maxPriceValue: number; productsActiveTab: ProductTabs; priceSchedulingEnabled: boolean };
    userStatuses: {
        allStatus: UserStatuses[];
        managerStatus: UserStatuses[];
        vendorStatus: UserStatuses[];
        vendorUserStatus: UserStatuses[];
        partnerStatus: UserStatuses[];
        partnerUserStatus: UserStatuses[];
    };
    organizations: string[];
    beneficiariesImportMapping: Record<number, string>;
    removeEntitlementsImportMapping: Record<number, string>;
    beneficiaryPrimaryIdentifierType: BeneficiaryPrimaryIdentifierType;
    supportEmail: string;
    hideBeneficiarySupportTab: boolean;
    documentsUseExternalLinks: boolean;

    constructor(appConfig: AppConfigState) {
        this.entitlementsConfig = appConfig.entitlementsConfig;
        this.sapConfig = appConfig.sapConfig;
        this.entitlementCurrencyConfig = appConfig.entitlementCurrencyConfig;
        this.paymentConfig = appConfig.paymentConfig;
        this.country = appConfig.country;
        this.timeZone = appConfig.timeZone;
        this.permissions = appConfig.permissions;
        this.initialTransactionDate = appConfig.initialTransactionDate;
        this.userStatuses = appConfig.userStatuses;
        this.productManagementConfig = appConfig.productManagementConfig;
        this.organizations = appConfig.organizations;
        this.beneficiariesImportMapping = appConfig.beneficiariesImportMapping;
        this.removeEntitlementsImportMapping = appConfig.removeEntitlementsImportMapping;
        this.beneficiaryPrimaryIdentifierType = appConfig.beneficiaryPrimaryIdentifierType;
        this.supportEmail = appConfig.supportEmail;
        this.hideBeneficiarySupportTab = appConfig.hideBeneficiarySupportTab;
        this.documentsUseExternalLinks = appConfig.documentsUseExternalLinks;
    }
}

export class SapConfig {
    isSapActive: boolean;
    sapDocumentChainMinimumAmount: string;
    maxPOnumber: number;

    constructor(isSapActive = false, sapDocumentChainMinimumAmount = '100', maxPOnumber = 20) {
        this.isSapActive = isSapActive;
        this.sapDocumentChainMinimumAmount = sapDocumentChainMinimumAmount;
        this.maxPOnumber = maxPOnumber;
    }
}

export class EntitlementCategoriesConfig {
    categories: string[];
    categoriesToCategoriesFullNames: { [name: string]: string };
    categoriesToDeduplicationFlags: { [name: string]: UaopFlags[] };
    categoryConfig: SingleCategoryConfig[];
    metaCategoryConfig: SingleMetaCategory[];

    constructor(
        categories: string[] = [],
        categoriesToCategoriesFullNames: { [name: string]: string } = {},
        categoriesToDeduplicationFlags: { [name: string]: UaopFlags[] } = {},
        categoryConfig: SingleCategoryConfig[] = [],
        metaCategoryConfig: SingleMetaCategory[] = []
    ) {
        this.categories = categories;
        this.categoriesToCategoriesFullNames = categoriesToCategoriesFullNames;
        this.categoriesToDeduplicationFlags = categoriesToDeduplicationFlags;
        this.categoryConfig = categoryConfig;
        this.metaCategoryConfig = metaCategoryConfig;
    }
}

export interface PredefinedCurrency {
    name: string;
    fractionDigits: number;
    locales?: string;
    exchangeRates?: { [currency: string]: number };
}

export class EntitlementCurrencyConfig implements PredefinedCurrency {
    name: string;
    fractionDigits: number;
    locales?: string;
    exchangeRates?: { [currency: string]: number };

    constructor(name = '', fractionDigits = 3, locales = '', exchangeRates = {}) {
        this.name = name;
        this.fractionDigits = fractionDigits;
        this.locales = locales;
        this.exchangeRates = exchangeRates;
    }
}

export class PaymentConfig {
    initialPeriodEnd: string;
    duration: PaymentDuration;
    mergeArraysSize: string;
}

export interface PaymentDuration {
    amount: DurationInputArg1;
    unit: DurationInputArg2;
}

export const AppConfigActionTypes = {
    loadAppConfig: 'AppConfig.loadAppConfig',
    changeProductTab: 'AppConfig.changeProductTab',
};

export const AppConfigActionCreators = {
    loadApplicationConfig(isVendor?: boolean, isPartner?: boolean) {
        let getConfig;

        if (isPartner) {
            getConfig = getAppPartnerConfig;
        } else {
            getConfig = getAppConfig;
        }
        return (dispatch) => {
            return getConfig().then((res) => {
                dispatch({
                    type: AppConfigActionTypes.loadAppConfig,
                    payload: res,
                });
            });
        };
    },
};

export function appConfigReducer(
    state: AppConfigState = new AppConfigState({
        entitlementsConfig: new EntitlementCategoriesConfig(),
        sapConfig: new SapConfig(),
        entitlementCurrencyConfig: new EntitlementCurrencyConfig(),
        paymentConfig: new PaymentConfig(),
        country: '',
        timeZone: 'utc',
        permissions: {},
        initialTransactionDate: '',
        userStatuses: {
            allStatus: [],
            managerStatus: [],
            vendorStatus: [],
            vendorUserStatus: [],
            partnerStatus: [],
            partnerUserStatus: [],
        },
        productManagementConfig: {
            maxPriceValue: Number.MAX_SAFE_INTEGER,
            productsActiveTab: ProductTabs.Prices,
            priceSchedulingEnabled: false,
        },
        organizations: [],
        beneficiariesImportMapping: {},
        removeEntitlementsImportMapping: {},
        beneficiaryPrimaryIdentifierType: null,
        supportEmail: '',
        hideBeneficiarySupportTab: false,
        documentsUseExternalLinks: false,
    }),
    action: Action
) {
    switch (action.type) {
        case AppConfigActionTypes.loadAppConfig:
            return new AppConfigState(action.payload);
        case AppConfigActionTypes.changeProductTab:
            return new AppConfigState({
                ...state,
                productManagementConfig: { ...state.productManagementConfig, productsActiveTab: action.payload },
            });
    }
    return state;
}
